import * as React from 'react';

import type { Typography2Variant } from '@coursera/cds-core';

import getProductTitle from 'bundles/unified-common/utils/getProductTitle';
import ArticlesRecommendationsModuleNotConfigurable from 'bundles/unified-description-page-common/components/recommendations/ArticlesRecommendationsModuleNotConfigurable';
import { useArticlesRecommendations } from 'bundles/unified-description-page-common/contexts/consumer/ArticlesRecommendationsContext';
import { useXdpSubscriptionsForwardDiscovery } from 'bundles/unified-description-page-common/providers/SubscriptionsForwardDiscoveryDataProvider';
import type { ConsumerCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerCoursePageQuery';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';

// eslint-disable-next-line @coursera-web/custom-rules/i18n-import-rule
import _t from 'i18n!nls/xdp';

type PageData = ConsumerSpecializationPageFieldsFragment | ConsumerCoursePageFieldsFragment;

type Props = {
  pageData: Pick<PageData, 'name' | 'domains' | 'slug'> & {
    productVariant?: ConsumerSpecializationPageFieldsFragment['productVariant'];
  };
  config?: {
    title?: string;
    titleTypographyVariant?: Typography2Variant;
  };
};

const ArticlesRecommendationsModuleRenderer = ({ pageData, config }: Props) => {
  const articlesRecommendations = useArticlesRecommendations();
  const productTitle = getProductTitle(pageData.name, pageData.productVariant);
  const { shouldShowXdpChanges } = useXdpSubscriptionsForwardDiscovery();

  let title = _t('New to #{subdomainOrDomainName}? Start here.', {
    subdomainOrDomainName: pageData.domains?.[0]?.subdomainName || pageData.domains?.[0]?.domainName,
  });
  if (config?.title) {
    title = config.title;
  }
  if (shouldShowXdpChanges) {
    title = _t('Career resources');
  }

  return (
    <ArticlesRecommendationsModuleNotConfigurable
      recommendations={articlesRecommendations?.recommendations}
      isRecommendationsLoading={!!articlesRecommendations?.isArticlesRecsLoading}
      title={title}
      titleTypographyVariant={config?.titleTypographyVariant}
      trackingData={{ slug: pageData.slug, productTitle }}
    />
  );
};

export default ArticlesRecommendationsModuleRenderer;
